import { CompanyQueueStatus } from '@/components/company/CompanyQueueStatus';
import { CompanyQueueIntro } from '@/components/company/CompanyQueueIntro';
import { CompanyQueueServices } from '@/components/company/CompanyQueueServices';
import { BookingFlow } from '@/components/booking/BookingFlow';
import { CompanyInactiveState } from '@/components/company/CompanyInactiveState';
import { CompanyInfoContent, CompanyInfoPanel } from '@/components/company/CompanyInfoPanel';
import { CompanyPageErrorState } from '@/components/company/CompanyPageErrorState';
import { CompanyPageLoadingState } from '@/components/company/CompanyPageLoadingState';
import { CompanyReviews } from '@/components/company/CompanyReviews';
import Header from '@/components/Header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCompanyBySlug } from '@/hooks/queries/useCompanyBySlug';
import { useCompanyProfessionals } from '@/hooks/queries/useCompanyProfessionals';
import { useCompanyServices } from '@/hooks/queries/useCompanyServices';
import { resolveCompanyAttendanceCapabilities } from '@/lib/company/capabilities';
import { buildCompanyClaimWhatsAppUrl } from '@/lib/company/claim';
import { canCompanyAcceptBookings } from '@/lib/company/status';
import { getCompanyOpenStatus, groupCompanyWorkingHours } from '@/lib/company/working-hours';
import { resolveCompanyBanner } from '@/lib/image-placeholders';
import type { CompanyActiveMode, CompanyTab } from '@/types/company-page';
import { ArrowRight, CalendarCheck, ListChecks, MapPin, Star } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const desktopQuery = '(min-width: 1024px)';
const subscribeToDesktop = (onChange: () => void) => {
  const query = window.matchMedia(desktopQuery);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
};
const getIsDesktop = () => window.matchMedia(desktopQuery).matches;

const CompanyPage = () => {
  const {
    navigate,
    company,
    isLoading,
    isError,
    refetch,
    currentDateTime,
    bookingSectionRef,
    fullAddress,
    companyWorkingHours,
    companyOpenStatus,
    companyClaimWhatsAppUrl,
    shouldShowScheduleAction,
    shouldShowChoiceHeading,
    activeTab,
    isWalkInActive,
    services,
    servicesQuery,
    professionals,
    handleSchedule,
    handleTabChange,
    handleWalkIn,
  } = useCompanyPageModel();
  if (isLoading) {
    return <CompanyPageLoadingState />;
  }

  if (isError || !company) {
    return <CompanyPageErrorState onRetry={() => void refetch()} onGoBack={() => navigate(-1)} />;
  }

  if (!canCompanyAcceptBookings(company)) {
    return (
      <CompanyInactiveState
        key={company.id}
        company={company}
        fullAddress={fullAddress}
        whatsappUrl={companyClaimWhatsAppUrl}
      />
    );
  }

  return (
    <div className="bg-background min-h-screen pb-10">
      <Header showBack />

      <div className="relative min-h-64 overflow-hidden md:min-h-72 lg:min-h-80">
        <img
          src={resolveCompanyBanner(company.coverPhoto)}
          alt={company.name}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="relative container pt-20 pb-6 md:pt-32">
          <div>
            <h1 className="text-2xl font-bold text-white md:text-3xl">{company.name}</h1>
            <div className="mt-3 flex flex-wrap gap-2.5">
              <div className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 backdrop-blur-sm">
                <Star className="fill-warning text-warning h-4 w-4" />
                <span className="text-sm font-semibold text-white">{company.rating}</span>
                <span className="text-xs text-white/80">({company.ratingCount} avaliações)</span>
              </div>
              <div className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 backdrop-blur-sm">
                <MapPin className="h-3.5 w-3.5 text-white/80" />
                <span className="text-sm text-white/90">{company.neighborhood}</span>
              </div>
              <span
                className={
                  companyOpenStatus.isOpen
                    ? 'bg-success text-success-foreground inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium'
                    : 'bg-muted text-muted-foreground inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium'
                }
              >
                {companyOpenStatus.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-6">
        <div className="lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(380px,1fr)] lg:gap-8 xl:grid-cols-[minmax(0,2fr)_minmax(420px,1fr)]">
          <main className="min-w-0 space-y-6 sm:space-y-8">
            <CompanyQueueStatus companyId={company.id} slug={company.slug} />
            {isWalkInActive && <CompanyQueueIntro onJoin={handleWalkIn} />}
            {shouldShowChoiceHeading && (
              <section className="space-y-4">
                <div>
                  <h2 className="text-foreground text-xl font-bold">Como você quer ser atendido?</h2>
                  <p className="text-muted-foreground mt-1 text-sm">Agende um horário ou entre na fila presencial.</p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={handleSchedule}
                    className="border-border bg-card hover:border-primary/40 rounded-xl border p-5 text-left transition-[color,background-color,border-color,box-shadow,opacity,transform,translate] hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <span className="bg-primary/10 text-primary flex h-11 w-11 items-center justify-center rounded-xl">
                      <CalendarCheck className="h-5 w-5" />
                    </span>
                    <h3 className="text-card-foreground mt-4 text-lg font-semibold">Agendar horário</h3>
                    <p className="text-muted-foreground mt-2 text-sm">Escolha serviço, profissional e horário.</p>
                    <span className="text-card-foreground mt-5 inline-flex items-center gap-2 text-sm font-medium">
                      Ver horários
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={handleWalkIn}
                    className="border-border bg-card hover:border-primary/40 rounded-xl border p-5 text-left transition-[color,background-color,border-color,box-shadow,opacity,transform,translate] hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <span className="bg-primary/10 text-primary flex h-11 w-11 items-center justify-center rounded-xl">
                      <ListChecks className="h-5 w-5" />
                    </span>
                    <h3 className="text-card-foreground mt-4 text-lg font-semibold">Fila presencial</h3>
                    <p className="text-muted-foreground mt-2 text-sm">Entre na fila para atendimento no local.</p>
                    <span className="text-card-foreground mt-5 inline-flex items-center gap-2 text-sm font-medium">
                      Entrar na fila
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </button>
                </div>
              </section>
            )}

            <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-5">
              <TabsList className="border-border bg-background h-auto min-h-11 w-full max-w-full justify-start gap-5 overflow-x-auto rounded-none border-b p-0">
                {(shouldShowScheduleAction || isWalkInActive) && (
                  <TabsTrigger
                    value="services"
                    className="data-[state=active]:border-primary data-[state=active]:text-primary min-h-12 shrink-0 rounded-none border-b-2 border-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Serviços
                  </TabsTrigger>
                )}

                {professionals.length > 0 && (
                  <TabsTrigger
                    value="professionals"
                    className="data-[state=active]:border-primary data-[state=active]:text-primary min-h-12 shrink-0 rounded-none border-b-2 border-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Profissionais
                  </TabsTrigger>
                )}

                <TabsTrigger
                  value="reviews"
                  className="data-[state=active]:border-primary data-[state=active]:text-primary min-h-12 shrink-0 rounded-none border-b-2 border-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  Avaliações
                </TabsTrigger>

                <TabsTrigger
                  value="info"
                  className="data-[state=active]:border-primary data-[state=active]:text-primary min-h-12 shrink-0 rounded-none border-b-2 border-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none lg:hidden"
                >
                  Informações
                </TabsTrigger>
              </TabsList>

              {shouldShowScheduleAction && (
                <TabsContent value="services" className="mt-0">
                  <section ref={bookingSectionRef} className="border-border bg-card rounded-xl border p-5">
                    <div className="mb-5">
                      <h2 className="text-card-foreground text-xl font-bold">Serviços</h2>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Selecione os serviços para ver os horários disponíveis.
                      </p>
                    </div>
                    <BookingFlow
                      company={company}
                      enabled={activeTab === 'services'}
                      onWalkInSelected={handleWalkIn}
                      requireEntryModeSelection={shouldShowChoiceHeading}
                      variant="embedded"
                    />
                  </section>
                </TabsContent>
              )}

              {isWalkInActive && (
                <TabsContent value="services" className="mt-0">
                  <CompanyQueueServices
                    services={services}
                    isLoading={servicesQuery.isLoading}
                    isError={servicesQuery.isError}
                    onRetry={() => void servicesQuery.refetch()}
                    onSelect={handleWalkIn}
                  />
                </TabsContent>
              )}

              {professionals.length > 0 && (
                <TabsContent value="professionals" className="mt-0">
                  <section className="space-y-3">
                    <h2 className="text-foreground text-xl font-bold">Profissionais</h2>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {professionals.map((professional) => (
                        <div
                          key={professional.id}
                          className="border-border bg-card flex items-center gap-4 rounded-xl border p-4"
                        >
                          <img
                            src={professional.photo ?? '/person-placeholder.jpg'}
                            alt={professional.name}
                            className="h-14 w-14 shrink-0 rounded-full object-cover"
                          />
                          <div className="min-w-0">
                            <h3 className="text-card-foreground text-base font-semibold">{professional.name}</h3>
                            <div className="mt-1 flex items-center gap-1">
                              <Star className="fill-warning text-warning h-3.5 w-3.5" />
                              <span className="text-card-foreground text-sm font-medium">{professional.rating}</span>
                              <span className="text-muted-foreground text-xs">({professional.feedbackCount})</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                </TabsContent>
              )}

              <TabsContent value="reviews" className="mt-0">
                <CompanyReviews
                  companyId={company.id}
                  enabled={activeTab === 'reviews'}
                  fallbackRating={company.rating}
                  fallbackTotal={company.ratingCount}
                />
              </TabsContent>

              <TabsContent value="info" className="mt-0">
                <section className="border-border bg-card space-y-4 rounded-xl border p-5 lg:hidden">
                  <CompanyInfoContent
                    currentDateTime={currentDateTime}
                    fullAddress={fullAddress}
                    latitude={company.latitude}
                    longitude={company.longitude}
                    phoneNumber={company.phoneNumber}
                    amenities={company.amenities}
                    paymentMethods={company.paymentMethods}
                    instagramUrl={company.instagramUrl}
                    facebookUrl={company.facebookUrl}
                    whatsappNumber={company.whatsappNumber}
                    workingHours={companyWorkingHours}
                  />
                </section>
              </TabsContent>
            </Tabs>
          </main>

          <aside className="hidden lg:block">
            <div className="sticky top-20">
              <CompanyInfoPanel
                currentDateTime={currentDateTime}
                fullAddress={fullAddress}
                latitude={company.latitude}
                longitude={company.longitude}
                phoneNumber={company.phoneNumber}
                amenities={company.amenities}
                paymentMethods={company.paymentMethods}
                instagramUrl={company.instagramUrl}
                facebookUrl={company.facebookUrl}
                whatsappNumber={company.whatsappNumber}
                workingHours={companyWorkingHours}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CompanyPage;

function useCompanyPageModel() {
  const isDesktop = useSyncExternalStore(subscribeToDesktop, getIsDesktop);
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { company, isLoading, isError, refetch } = useCompanyBySlug(slug);
  const companyCapabilities = resolveCompanyAttendanceCapabilities(company);
  const [currentDateTime, setCurrentDateTime] = useState(() => new Date());
  const [activeSelection, setActiveSelection] = useState<{
    companyId?: string;
    mode?: CompanyActiveMode;
    tab?: CompanyTab;
  }>({});
  const bookingSectionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);
  const fullAddress = useMemo(() => {
    const addressParts = [company?.street, company?.number, company?.neighborhood].filter(Boolean);
    const cityAndState = [company?.city?.name, company?.city?.state?.stateCode].filter(Boolean).join(' - ');

    if (cityAndState) {
      addressParts.push(cityAndState);
    }

    if (company?.postalCode) {
      addressParts.push(`CEP ${company.postalCode}`);
    }

    return addressParts.join(', ');
  }, [company]);
  const companyWorkingHours = useMemo(() => groupCompanyWorkingHours(company?.workingHours), [company?.workingHours]);
  const companyOpenStatus = useMemo(
    () => getCompanyOpenStatus(companyWorkingHours, currentDateTime),
    [companyWorkingHours, currentDateTime],
  );
  const companyClaimWhatsAppUrl = useMemo(
    () => buildCompanyClaimWhatsAppUrl(company?.whatsappNumber, company?.name),
    [company?.name, company?.whatsappNumber],
  );
  const shouldShowScheduleAction = companyCapabilities.allowScheduledBookings;
  const shouldShowWalkInAction = companyCapabilities.allowWalkInQueue;
  const shouldShowChoiceHeading = shouldShowScheduleAction && shouldShowWalkInAction;
  const defaultActiveMode = getDefaultActiveMode(shouldShowScheduleAction, shouldShowWalkInAction);
  const defaultActiveTab: CompanyTab = shouldShowScheduleAction || shouldShowWalkInAction ? 'services' : 'reviews';
  const activeMode =
    activeSelection.companyId === company?.id && activeSelection.mode ? activeSelection.mode : defaultActiveMode;
  const selectedTab =
    activeSelection.companyId === company?.id && activeSelection.tab ? activeSelection.tab : defaultActiveTab;
  const activeTab = isDesktop && selectedTab === 'info' ? defaultActiveTab : selectedTab;
  const isWalkInActive = shouldShowWalkInAction && activeMode === 'walk-in';
  const catalogCompanyId = company && canCompanyAcceptBookings(company) ? company.id : undefined;
  const servicesQuery = useCompanyServices(catalogCompanyId);
  const { services } = servicesQuery;
  const { professionals } = useCompanyProfessionals(catalogCompanyId);
  const handleSchedule = () => {
    setActiveSelection({
      companyId: company?.id,
      mode: 'schedule',
      tab: 'services',
    });
    window.requestAnimationFrame(() => {
      bookingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const handleTabChange = (nextTab: string) => {
    const companyTab = nextTab as CompanyTab;

    setActiveSelection((currentSelection) => ({
      companyId: company?.id,
      mode: currentSelection.companyId === company?.id ? currentSelection.mode : undefined,
      tab: companyTab,
    }));
  };
  const handleWalkIn = () => {
    if (!company) {
      return;
    }

    navigate(`/${company.slug}/walk-in`);
  };
  return {
    navigate,
    company,
    isLoading,
    isError,
    refetch,
    currentDateTime,
    bookingSectionRef,
    fullAddress,
    companyWorkingHours,
    companyOpenStatus,
    companyClaimWhatsAppUrl,
    shouldShowScheduleAction,
    shouldShowChoiceHeading,
    activeTab,
    isWalkInActive,
    services,
    servicesQuery,
    professionals,
    handleSchedule,
    handleTabChange,
    handleWalkIn,
  };
}

function getDefaultActiveMode(schedule: boolean, walkIn: boolean): CompanyActiveMode {
  if (schedule && walkIn) return 'choice';
  if (schedule) return 'schedule';
  if (walkIn) return 'walk-in';
  return 'choice';
}
